import type { ResumeDto } from "@izicvs/dto";
import { useMutation, useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export type AtsCategoryKey =
  | "keywords"
  | "preferred"
  | "contact"
  | "quantification"
  | "length"
  | "verbs";

export type AtsCategory = { key: AtsCategoryKey; score: number; weight: number };

export type AtsStats = {
  wordCount: number;
  metrics: number;
  actionVerbs: number;
  hasEmail: boolean;
  hasPhone: boolean;
  requiredCount: number;
  matchedRequired: number;
  preferredCount: number;
  matchedPreferred: number;
};

export type AtsScore = {
  score: number;
  categories: AtsCategory[];
  matchedKeywords: string[];
  missingKeywords: string[];
  stats: AtsStats;
};

export type RecruiterReview = {
  shortlist: "yes" | "maybe" | "no";
  verdict: string;
  strengths: string[];
  concerns: string[];
};

export type CvAnalysis = {
  overall: number;
  strengths: string[];
  weaknesses: string[];
  sectionFeedback: { section: string; feedback: string }[];
};

export type InterviewPrep = {
  questions: { question: string; focus: string }[];
  tips: string[];
};

export type CareerAnalysis = {
  ats: AtsScore;
  recruiter: RecruiterReview;
  analysis: CvAnalysis;
  interview: InterviewPrep;
  // Present on a freshly-run analysis, to attach a cover letter to it.
  analysisId?: string;
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Serialize the structured resume into clean plain text for the AI experts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resumeToPlainText = (data: any): string => {
  const parts: string[] = [];
  const basics = data?.basics ?? {};

  parts.push(
    [basics.name, basics.headline, basics.email, basics.phone, basics.location]
      .filter(Boolean)
      .join(" "),
  );

  const sections = data?.sections ?? {};
  for (const key of Object.keys(sections)) {
    const section = sections[key];
    if (!section) continue;

    if (typeof section.content === "string") parts.push(stripHtml(section.content));

    if (Array.isArray(section.items)) {
      for (const item of section.items) {
        parts.push(
          [
            item.name,
            item.position,
            item.company,
            item.title,
            item.headline,
            item.institution,
            item.studyType,
            item.summary && stripHtml(item.summary),
            item.description && stripHtml(item.description),
            Array.isArray(item.keywords) ? item.keywords.join(" ") : "",
            item.date,
          ]
            .filter(Boolean)
            .join(" "),
        );
      }
    }
  }

  return parts.filter(Boolean).join("\n");
};

type JobMatchPayload = {
  resume: string;
  jobDescription: string;
  locale?: string;
  resumeId?: string;
  resumeTitle?: string;
};

// Full multi-expert analysis in a single call. `locale` is the active UI
// language, so the AI output matches what the user is viewing.
export const analyzeResume = async (payload: JobMatchPayload) => {
  const response = await axios.post<CareerAnalysis>("/ai/analyze", payload);

  return response.data;
};

export const useAnalyzeResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: analyzeResumeFn,
  } = useMutation({
    mutationFn: analyzeResume,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["job-analyses"] });
    },
  });

  return { analyzeResume: analyzeResumeFn, loading, error };
};

export type JobAnalysisSummary = {
  id: string;
  resumeId: string | null;
  resumeTitle: string;
  jobTitle: string;
  atsScore: number;
  shortlist: "yes" | "maybe" | "no";
  createdAt: string;
};

export type JobAnalysisRecord = JobAnalysisSummary & {
  result: CareerAnalysis;
  coverLetter: string | null;
  legitimacy: LegitimacyCheck | null;
  applicationAnswers: ApplicationAnswers | null;
  outreach: Outreach | null;
};

export const fetchJobAnalyses = async () => {
  const response = await axios.get<JobAnalysisSummary[]>("/ai/analyses");

  return response.data;
};

export const useJobAnalyses = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["job-analyses"],
    queryFn: fetchJobAnalyses,
  });

  return { analyses: data, loading: isLoading };
};

export const fetchJobAnalysis = async (id: string) => {
  const response = await axios.get<JobAnalysisRecord>(`/ai/analyses/${id}`);

  return response.data;
};

export const deleteJobAnalysis = async (id: string) => {
  await axios.delete(`/ai/analyses/${id}`);

  return id;
};

export const useDeleteJobAnalysis = () => {
  const { mutateAsync: deleteAnalysisFn } = useMutation({
    mutationFn: deleteJobAnalysis,
    onSuccess: (id) => {
      queryClient.setQueryData<JobAnalysisSummary[]>(["job-analyses"], (cache) =>
        cache?.filter((analysis) => analysis.id !== id),
      );
    },
  });

  return { deleteAnalysis: deleteAnalysisFn };
};

export const generateCoverLetter = async (payload: {
  resume: string;
  jobDescription: string;
  locale?: string;
  analysisId?: string;
}) => {
  const response = await axios.post<{ text: string }>("/ai/cover-letter", payload);

  return response.data.text;
};

export const useCoverLetter = () => {
  const { mutateAsync: coverLetter, isPending: loading } = useMutation({
    mutationFn: generateCoverLetter,
  });

  return { coverLetter, loading };
};

export type LegitimacyCheck = {
  risk: "low" | "medium" | "high";
  verdict: string;
  redFlags: string[];
  greenFlags: string[];
};

export const checkLegitimacy = async (payload: {
  resume: string;
  jobDescription: string;
  locale?: string;
  analysisId?: string;
}) => {
  const response = await axios.post<LegitimacyCheck>("/ai/legitimacy", payload);
  return response.data;
};

export const useLegitimacy = () => {
  const { mutateAsync: check, isPending: loading } = useMutation({ mutationFn: checkLegitimacy });
  return { check, loading };
};

export type ApplicationAnswers = { answers: { question: string; answer: string }[] };

export const generateAnswers = async (payload: {
  resume: string;
  jobDescription: string;
  locale?: string;
  analysisId?: string;
}) => {
  const response = await axios.post<ApplicationAnswers>("/ai/application-answers", payload);
  return response.data;
};

export const useApplicationAnswers = () => {
  const { mutateAsync: generate, isPending: loading } = useMutation({
    mutationFn: generateAnswers,
  });
  return { generate, loading };
};

export type Outreach = {
  contacts: { role: string; whereToFind: string; searchQuery: string }[];
  linkedinMessage: string;
  email: { subject: string; body: string };
};

export const generateOutreach = async (payload: {
  resume: string;
  jobDescription: string;
  locale?: string;
  analysisId?: string;
}) => {
  const response = await axios.post<Outreach>("/ai/outreach", payload);
  return response.data;
};

export const useOutreach = () => {
  const { mutateAsync: generate, isPending: loading } = useMutation({
    mutationFn: generateOutreach,
  });
  return { generate, loading };
};

export type TailoredResume = {
  resume: ResumeDto;
  changes: string[];
  attentionPoints: string[];
};

type TailorPayload = { resumeId: string; jobDescription: string; locale?: string };

// Create a new resume tailored to a job, based on an existing one. The original
// is never modified; the new resume lands in the user's resume list.
export const tailorResumeToJob = async (payload: TailorPayload) => {
  const response = await axios.post<TailoredResume>("/ai/tailor-resume", payload);

  return response.data;
};

export const useTailorResumeToJob = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: tailorResumeFn,
  } = useMutation({
    mutationFn: tailorResumeToJob,
    onSuccess: ({ resume }) => {
      queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) =>
        cache ? [...cache, resume] : [resume],
      );
    },
  });

  return { tailorResume: tailorResumeFn, loading, error };
};

// --- Interview simulator: score a single answer with the STAR method ---

export type InterviewFeedback = {
  score: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
  sampleAnswer: string;
};

type InterviewAnswerPayload = {
  resume: string;
  jobDescription: string;
  question: string;
  answer: string;
  locale?: string;
};

export const scoreInterviewAnswer = async (payload: InterviewAnswerPayload) => {
  const response = await axios.post<InterviewFeedback>("/ai/interview-answer", payload);

  return response.data;
};

export const useInterviewAnswer = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: scoreAnswer,
  } = useMutation({ mutationFn: scoreInterviewAnswer });

  return { scoreAnswer, loading, error };
};
